import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { QueryService } from './query.service';
import { ChatMessage, ChatRequest, ChatResponse } from './chat-request.model';
import * as fromUser from '../user-info';

@Component({
  selector: 'ef-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  standalone: false
})
export class ChatComponent implements OnInit {
  /** When true, chat is shown in the app shell floating panel (close control + layout tweaks). */
  @Input() floatingPanel = false;
  @Output() closeFloatingPanel = new EventEmitter<void>();

  messages: ChatMessage[] = [];
  currentQuery: string = '';
  isLoading: boolean = false;
  error: string | null = null;
  employeeListID: string = '';
  
  @ViewChild('messageContainer') private messageContainer!: ElementRef;
  @ViewChildren('messageEl') private messageElements!: QueryList<ElementRef>;
  showInstructions: boolean = false;

  constructor(
    private queryService: QueryService,
    private userStore: Store<fromUser.State>
  ) {}

  ngOnInit(): void {
    // Component initialized
    this.userStore
      .pipe(
        select(fromUser.getUserInfo),
        filter(userInfo => !!userInfo && !!userInfo.EmployeeListID),
        take(1)
      )
      .subscribe(userInfo => {
        this.employeeListID = userInfo.EmployeeListID;
      });
  }

  async sendMessage(): Promise<void> {
    if (!this.currentQuery.trim() || this.isLoading) {
      return;
    }

    const userMessage: ChatMessage = {
      role: 'user',
      content: this.currentQuery,
      timestamp: new Date()
    };

    this.messages.push(userMessage);
    this.currentQuery = '';
    this.isLoading = true;
    this.error = null;

    try {
      const request: ChatRequest = {
        query: userMessage.content,
        session_id: this.employeeListID
      };

      const response: ChatResponse = await this.queryService.sendQuery(request);

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.response,
        sources: response.sources,
        timestamp: new Date()
      };

      this.messages.push(assistantMessage);

      this.scrollToLatestResponse();
    } catch (error: any) {
      this.error = error.message || 'Failed to get response. Please try again.';
      console.error('Error sending message:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /** Align the top of the latest message with the top of the scroll area. */
  private scrollToLatestResponse(): void {
    setTimeout(() => {
      const container = this.messageContainer?.nativeElement as HTMLElement;
      const lastMessage = this.messageElements?.last?.nativeElement as HTMLElement;
      if (!container || !lastMessage) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const messageRect = lastMessage.getBoundingClientRect();
      container.scrollTop += messageRect.top - containerRect.top;
    }, 100);
  }

  clearChat(): void {
    this.messages = [];
    this.error = null;
  }

  onCloseFloatingPanel(): void {
    this.closeFloatingPanel.emit();
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    event.preventDefault();
    void this.sendMessage();
  }

  formatMessage(content: string): string {
    if (!content) return '';
    
    // Escape HTML first to prevent XSS
    let formatted = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Convert markdown-style formatting to HTML
    // Process in order to avoid conflicts
    
    // Bold: **text** or __text__ (process before italic)
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__(.+?)__/g, '<strong>$1</strong>');
    
    // Italic: *text* or _text_ (single asterisk/underscore that aren't part of double)
    // Match single * or _ that aren't adjacent to another * or _
    formatted = formatted.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');
    formatted = formatted.replace(/(?<!_)_([^_\n]+?)_(?!_)/g, '<em>$1</em>');
    
    // Inline code: `text`
    formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>');
    
    // Line breaks
    formatted = formatted.replace(/\n/g, '<br>');
    
    return formatted;
  }
}